import { getUnixTime, isValid, parseISO } from 'date-fns';
import i18n from 'src/i18n';
import {
  ePlanCode,
  eSubscriptionStatus,
} from 'src/providers/SubscriptionProvider/Subscription.enums';
import { eUserRole } from 'src/providers/UserProvider/User.enums';

/**
 * @module hooks/Intercom/functions
 * Helper functions for transforming data to Intercom format
 */

/**
 * Format user role to human-readable string for Intercom
 * @param role - User role enum value
 * @returns Formatted role string
 */
export function formatUserRole(role: eUserRole): string {
  const roleMap: Record<eUserRole, string> = {
    [eUserRole.OWNER]: 'Owner',
    [eUserRole.ADMIN]: 'Admin',
    [eUserRole.SELLER]: 'Agent',
    [eUserRole.VIEWER]: 'Viewer',
  };

  return roleMap[role] || 'Agent';
}

/**
 * Map subscription status to Intercom format
 * @param status - Subscription status enum value
 * @returns Formatted status string
 */
export function mapSubscriptionStatus(status: eSubscriptionStatus): string {
  const statusMap: Record<eSubscriptionStatus, string> = {
    [eSubscriptionStatus.TRIAL]: 'Trial',
    [eSubscriptionStatus.ACTIVE]: 'Active',
    [eSubscriptionStatus.CANCELLED]: 'Cancelled',
    [eSubscriptionStatus.PAST_DUE]: 'Past Due',
    [eSubscriptionStatus.SCHEDULED_CANCELLATION]: 'Active',
    [eSubscriptionStatus.NO_PAID]: 'No Paid',
    [eSubscriptionStatus.DELETED]: 'Deleted',
    [eSubscriptionStatus.PAUSED]: 'Paused',
    [eSubscriptionStatus.INACTIVE]: 'Inactive',
  };

  return statusMap[status] || 'Unknown';
}

/**
 * Get plan name from plan code
 * @param planCode - Plan code enum value
 * @returns Plan name string
 */
export function getPlanName(planCode: ePlanCode): string {
  const planMap: Record<ePlanCode, string> = {
    [ePlanCode.PLAN_LEGACY]: 'Legacy',
    [ePlanCode.PLAN_BASIC]: 'Basic',
    [ePlanCode.PLAN_PROFESSIONAL]: 'Pro',
    [ePlanCode.PLAN_ENTERPRISE]: 'Advance',
  };

  return planMap[planCode] || 'Unknown';
}

/**
 * Extract email domain from email address
 * @param email - Email address
 * @returns Email domain or undefined
 */
export function extractEmailDomain(
  email: string | undefined,
): string | undefined {
  if (!email) return undefined;
  const parts = email.split('@');
  return parts.length === 2 ? parts[1] : undefined;
}

/**
 * Convert date string to Unix timestamp (seconds)
 * @param dateString - Date string or Date object
 * @returns Unix timestamp in seconds
 */
export function dateToUnixTimestamp(
  dateString: string | Date | undefined,
): number | undefined {
  if (!dateString) return undefined;

  const date =
    typeof dateString === 'string' ? parseISO(dateString) : dateString;
  if (!isValid(date)) {
    console.error('Invalid date provided to dateToUnixTimestamp');
    return undefined;
  }
  return getUnixTime(date);
}

/**
 * Convert cents to dollars
 * @param cents - Amount in cents
 * @returns Amount in dollars
 */
export function centsToDollars(cents: number | undefined): number | undefined {
  if (cents === undefined || cents === null) return undefined;
  return cents / 100;
}

/**
 * Count users from workspace info
 * @param users - Users object from workspace info
 * @returns Number of users or undefined
 */
export function countWorkspaceUsers(
  users: { [userAuthId: string]: unknown } | 0,
): number | 0 {
  if (!users) return 0;
  return Object.keys(users).length;
}

/**
 * Convert date to locale-formatted date string using i18n language setting
 * @param dateString - Date string or Date object
 * @param locale - Optional locale override (defaults to current i18n language)
 * @returns Formatted date string or undefined
 */
export function dateToLocaleDateString(
  dateString: string | Date | undefined,
  locale?: string,
): string | undefined {
  if (!dateString) return undefined;

  try {
    const date =
      typeof dateString === 'string' ? new Date(dateString) : dateString;

    if (isNaN(date.getTime())) {
      return undefined;
    }

    // Map i18n language codes to Intl locales
    const languageToLocale: Record<string, string> = {
      es: 'es-MX',
      en: 'en-US',
      br: 'pt-BR',
    };

    // Use provided locale or get from i18n
    const currentLanguage = locale ? locale : i18n.language || 'es';

    // Map language code to full locale if needed
    const intlLocale =
      languageToLocale[currentLanguage] ||
      languageToLocale[currentLanguage.split('-')[0]] ||
      'es-MX';

    const formatted = date.toLocaleDateString(intlLocale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    // For Spanish and Portuguese, add "de" between day and month, and month and year
    // to match the format used in the cancellation flow
    if (intlLocale === 'es-MX' || intlLocale === 'pt-BR') {
      return formatted.replace(/(\d+)\s+(\w+)\s+(\d+)/, '$1 de $2 de $3');
    }

    return formatted;
  } catch (error) {
    console.error('Error converting date to locale string:', error);
    return undefined;
  }
}

/**
 * Calculate days remaining until subscription end date
 * @param endDate - Subscription end date
 * @returns Number of days remaining, or null if invalid
 */
export function calculateDaysUntilSubscriptionEnd(
  endDate: Date | string | undefined,
): number | null {
  if (!endDate) return null;

  try {
    const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

    if (isNaN(end.getTime())) {
      return null;
    }

    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  } catch (error) {
    console.error('Error calculating days until subscription end:', error);
    return null;
  }
}
